import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useCompartilhamentos, CompartilhadaBadge } from './CompartilhadaBadge';

const BrasilSatPage = () => {
  const compartilhamentos = useCompartilhamentos();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [vincularMoto, setVincularMoto] = useState(null);
  const [imeiSelecionado, setImeiSelecionado] = useState('');
  const [salvandoVinculo, setSalvandoVinculo] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [recalculando, setRecalculando] = useState(false);
  const [busca, setBusca] = useState('');
  
  // Estados para modal de confirmação de manutenção
  const [showManutencaoModal, setShowManutencaoModal] = useState(false);
  const [motoManutencao, setMotoManutencao] = useState(null);
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [servicosCustomizados, setServicosCustomizados] = useState([]);
  const [itensEstoque, setItensEstoque] = useState([]);
  const [novoServicoNome, setNovoServicoNome] = useState('');
  const [novoServicoValor, setNovoServicoValor] = useState('');
  const [salvandoManutencao, setSalvandoManutencao] = useState(false);
  
  // Estado para confirmação extra antes de salvar
  const [showConfirmacaoFinal, setShowConfirmacaoFinal] = useState(false);
  
  // Estado para modal de manutenção avulsa (sem afetar KM)
  const [showManutencaoAvulsa, setShowManutencaoAvulsa] = useState(false);
  const [motoAvulsa, setMotoAvulsa] = useState(null);
  const [tipoManutencaoAvulsa, setTipoManutencaoAvulsa] = useState('');
  const [descricaoAvulsa, setDescricaoAvulsa] = useState('');
  const [valorAvulsa, setValorAvulsa] = useState('');
  const [salvandoAvulsa, setSalvandoAvulsa] = useState(false);

  // Estado para modal de ajuste manual de próxima manutenção (KM-alvo)
  const [showAjusteManual, setShowAjusteManual] = useState(false);
  const [motoAjuste, setMotoAjuste] = useState(null);
  const [kmAjuste, setKmAjuste] = useState('');
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);

  const abrirAjusteManual = (moto) => {
    setMotoAjuste(moto);
    // Pré-preenche com o override atual se houver, senão com o KM atual + 1000
    const atual = moto.proxima_manutencao_km_manual;
    if (atual && Number(atual) > 0) {
      setKmAjuste(String(atual));
    } else {
      setKmAjuste(String((moto.km_atual || 0) + 1000));
    }
    setShowAjusteManual(true);
  };

  const salvarAjusteManual = async () => {
    if (!motoAjuste) return;
    const valor = Number(kmAjuste);
    if (!valor || valor <= 0) {
      alert('Informe um KM-alvo válido (maior que zero).');
      return;
    }
    setSalvandoAjuste(true);
    try {
      const r = await apiService.api.put(
        `/admin/brasilsat/moto/${motoAjuste.id}/proxima-manutencao-manual`,
        { proxima_manutencao_km: valor }
      );
      alert(r.data?.mensagem || 'Próxima manutenção definida com sucesso.');
      setShowAjusteManual(false);
      setMotoAjuste(null);
      setKmAjuste('');
      await carregarDados();
    } catch (e) {
      alert('❌ ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvandoAjuste(false);
    }
  };

  const removerAjusteManual = async () => {
    if (!motoAjuste) return;
    if (!window.confirm('Remover ajuste manual? A próxima manutenção voltará ao padrão de 2.000 km a partir da última realizada.')) return;
    setSalvandoAjuste(true);
    try {
      const r = await apiService.api.put(
        `/admin/brasilsat/moto/${motoAjuste.id}/proxima-manutencao-manual`,
        { proxima_manutencao_km: null }
      );
      alert(r.data?.mensagem || 'Override removido.');
      setShowAjusteManual(false);
      setMotoAjuste(null);
      setKmAjuste('');
      await carregarDados();
    } catch (e) {
      alert('❌ ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvandoAjuste(false);
    }
  };

  // ✏️ Definir KM ATUAL manualmente (sobrescreve sem passar pelo fator).
  // Útil quando há divergência entre BrasilSat e hodômetro físico.
  const editarKmManual = async (moto) => {
    const atual = moto.km_atual || 0;
    const novo = window.prompt(
      `✏️ Definir KM atual manualmente para a moto ${moto.placa}\n\n` +
      `KM atual no sistema: ${atual.toLocaleString()} km\n\n` +
      `Digite o NOVO valor exato (sem aplicar o fator de correção). ` +
      `O novo km também será enviado ao rastreador BrasilSat (SET_MILEAGE).`,
      String(atual)
    );
    if (novo === null) return; // cancelou
    const valor = Number(String(novo).replace(/[^\d]/g, ''));
    if (!Number.isFinite(valor) || valor < 0) {
      alert('❌ Valor inválido. Digite apenas números (km).');
      return;
    }
    if (!window.confirm(`Confirmar: alterar km de ${moto.placa} de ${atual.toLocaleString()} para ${valor.toLocaleString()} km?`)) {
      return;
    }
    try {
      const r = await apiService.api.put(
        `/admin/brasilsat/moto/${moto.id}/km-manual`,
        { km_atual: valor }
      );
      if (r.data?.sucesso) {
        const bs = r.data.brasilsat || {};
        let msgBs;
        if (bs.enviado && bs.confirmado) {
          msgBs = '📡 BrasilSat: hodômetro atualizado no rastreador ✅';
        } else if (bs.enviado) {
          msgBs = '📡 BrasilSat: comando enviado ao rastreador (aguardando confirmação do dispositivo)';
        } else {
          msgBs = `⚠️ BrasilSat NÃO atualizado: ${bs.motivo || 'erro desconhecido'}`;
        }
        const cal = r.data.calibracao || {};
        let msgCal;
        if (cal.calibrado) {
          const erroPct = ((1 / cal.fator - 1) * 100).toFixed(1);
          msgCal = `🎯 Rastreador calibrado! Fator ${cal.fator} (o GPS ${erroPct > 0 ? 'exagera' : 'subestima'} ~${Math.abs(erroPct)}%). O km passa a se ajustar sozinho.`;
        } else {
          msgCal = `ℹ️ Calibração: ${cal.motivo || 'não aplicada'}`;
        }
        alert(`✅ KM atualizado!\n\n${moto.placa}: ${r.data.km_anterior.toLocaleString()} → ${r.data.km_atual.toLocaleString()} km\n\n${msgBs}\n\n${msgCal}`);
        await carregarDados();
      } else {
        alert('❌ Erro: ' + (r.data?.erro || 'desconhecido'));
      }
    } catch (e) {
      alert('❌ Erro: ' + (e.response?.data?.detail || e.message));
    }
  };



  // Função para recalcular km_ultima_manutencao de todas as motos
  const recalcularManutencoes = async () => {
    try {
      setRecalculando(true);
      const response = await apiService.recalcularKmManutencao();
      
      if (response.sucesso) {
        alert(`✅ Recálculo concluído!\n\n${response.motos_atualizadas} moto(s) corrigida(s)\n${response.motos_sem_alteracao} moto(s) já estavam corretas`);
        // Recarregar dados
        await carregarDados();
      } else {
        alert('❌ Erro ao recalcular: ' + (response.erro || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao recalcular:', error);
      alert('❌ Erro ao recalcular manutenções: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRecalculando(false);
    }
  };

  // Função para abrir modal de manutenção avulsa
  const abrirManutencaoAvulsa = async (moto) => {
    setMotoAvulsa(moto);
    setTipoManutencaoAvulsa('');
    setDescricaoAvulsa('');
    setValorAvulsa('');
    setShowManutencaoAvulsa(true);
    // Mesma lista de itens do estoque (unificada com Confirmar Manutenção)
    try {
      const r = await apiService.api.get('/estoque');
      setItensEstoque(r.data?.itens || []);
    } catch (e) {
      console.error('Erro ao carregar estoque:', e);
      setItensEstoque([]);
    }
  };

  // Seleção do tipo (item do estoque) na manutenção avulsa
  const handleTipoAvulsa = (e) => {
    const v = e.target.value;
    setTipoManutencaoAvulsa(v);
    const item = itensEstoque.find(i => i.id === v);
    if (item && item.preco_unitario > 0) {
      setValorAvulsa(String(item.preco_unitario));
    }
  };

  // Função para salvar manutenção avulsa (SEM afetar KM)
  const salvarManutencaoAvulsa = async () => {
    if (!motoAvulsa || !tipoManutencaoAvulsa) {
      alert('Por favor, selecione o tipo de manutenção.');
      return;
    }

    try {
      setSalvandoAvulsa(true);

      const itemSel = itensEstoque.find(i => i.id === tipoManutencaoAvulsa);
      const tipoNome = itemSel ? itemSel.nome : tipoManutencaoAvulsa;

      const dados = {
        moto_id: motoAvulsa.id,
        tipo: tipoNome,
        descricao: descricaoAvulsa || tipoNome,
        valor: parseFloat(valorAvulsa) || 0,
        km_realizada: motoAvulsa.km_atual || 0,
        avulsa: true,  // Flag para indicar que NÃO deve atualizar km_ultima_manutencao
        item_estoque_id: itemSel ? itemSel.id : null,
        observacoes: 'Manutenção avulsa - não afeta ciclo de troca de óleo'
      };

      const resp = await apiService.registrarManutencaoAvulsa(dados);

      const descontados = resp?.estoque?.descontados || [];
      const alertasEstoque = resp?.estoque?.alertas || [];
      alert(
        `✅ Manutenção "${tipoNome}" registrada com sucesso!\n\nEsta manutenção NÃO afeta o ciclo de troca de óleo.` +
        (descontados.length > 0 ? `\n\n📦 Estoque abatido:\n${descontados.map(d => `• ${d.item}: -${d.quantidade_usada} (restam ${d.quantidade_restante})`).join('\n')}` : '') +
        (alertasEstoque.length > 0 ? `\n\n⚠️ ${alertasEstoque.map(a => a.mensagem).join('\n⚠️ ')}` : '')
      );
      setShowManutencaoAvulsa(false);
      setMotoAvulsa(null);
      await carregarDados();
    } catch (error) {
      console.error('Erro ao salvar manutenção avulsa:', error);
      alert('❌ Erro ao registrar manutenção: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSalvandoAvulsa(false);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBrasilSatStatus();
      setDados(response);
      setErro(null);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setErro(error.response?.data?.detail || 'Erro ao carregar dados do GPS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(carregarDados, 30000); // Atualizar a cada 30 segundos
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const abrirModalVinculo = (moto) => {
    setVincularMoto(moto);
    setImeiSelecionado(moto.gps_imei || '');
  };

  const salvarVinculo = async () => {
    if (!vincularMoto || !imeiSelecionado) return;
    
    try {
      setSalvandoVinculo(true);
      await apiService.vincularImeiMoto(vincularMoto.id, imeiSelecionado);
      
      await carregarDados();
      setVincularMoto(null);
      setImeiSelecionado('');
      alert('GPS vinculado com sucesso! Aguarde alguns minutos para a sincronização automática.');
    } catch (error) {
      console.error('Erro ao vincular:', error);
      alert('Erro ao vincular GPS. Tente novamente.');
    } finally {
      setSalvandoVinculo(false);
    }
  };

  const desvincularGps = async (moto) => {
    if (!confirm(`Desvincular GPS da moto ${moto.placa}?`)) return;
    
    try {
      await apiService.vincularImeiMoto(moto.id, '');
      await carregarDados();
      alert('GPS desvinculado com sucesso!');
    } catch (error) {
      console.error('Erro ao desvincular:', error);
      alert('Erro ao desvincular. Tente novamente.');
    }
  };

  const sincronizarAgora = async () => {
    try {
      setSincronizando(true);
      const response = await apiService.api.post('/admin/brasilsat/sincronizar-agora');
      
      if (response.data.sucesso) {
        alert(`Sincronização concluída! ${response.data.motos_atualizadas} moto(s) atualizada(s).`);
        await carregarDados();
      } else {
        alert(`Erro na sincronização: ${response.data.erro}`);
      }
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      alert('Erro ao forçar sincronização. Tente novamente.');
    } finally {
      setSincronizando(false);
    }
  };

  // Carrega serviços customizados do localStorage
  useEffect(() => {
    const saved = localStorage.getItem('servicos_manutencao_custom');
    if (saved) {
      setServicosCustomizados(JSON.parse(saved));
    }
  }, []);

  // Abre modal de confirmação de manutenção
  const abrirModalManutencao = async (moto) => {
    setMotoManutencao(moto);
    setServicosSelecionados([]);
    setNovoServicoNome('');
    setNovoServicoValor('');
    setShowManutencaoModal(true);
    // Carrega a lista de itens do estoque (mesma lista usada no abate)
    try {
      const r = await apiService.api.get('/estoque');
      setItensEstoque(r.data?.itens || []);
    } catch (e) {
      console.error('Erro ao carregar estoque:', e);
      setItensEstoque([]);
    }
  };

  // Toggle seleção de serviço
  const toggleServico = (servico) => {
    setServicosSelecionados(prev => {
      const exists = prev.find(s => s.id === servico.id);
      if (exists) {
        return prev.filter(s => s.id !== servico.id);
      } else {
        return [...prev, servico];
      }
    });
  };

  // Adiciona novo serviço customizado
  const adicionarServicoCustom = () => {
    if (!novoServicoNome.trim() || !novoServicoValor) return;
    
    const novoServico = {
      id: `custom_${Date.now()}`,
      nome: novoServicoNome.trim(),
      valor: parseFloat(novoServicoValor),
      custom: true
    };
    
    // Salva no localStorage para futuro uso
    const novosCustom = [...servicosCustomizados, novoServico];
    setServicosCustomizados(novosCustom);
    localStorage.setItem('servicos_manutencao_custom', JSON.stringify(novosCustom));
    
    // Já seleciona o novo serviço
    setServicosSelecionados(prev => [...prev, novoServico]);
    
    // Limpa campos
    setNovoServicoNome('');
    setNovoServicoValor('');
  };

  // Remove serviço customizado
  const removerServicoCustom = (servicoId) => {
    const novosCustom = servicosCustomizados.filter(s => s.id !== servicoId);
    setServicosCustomizados(novosCustom);
    localStorage.setItem('servicos_manutencao_custom', JSON.stringify(novosCustom));
    setServicosSelecionados(prev => prev.filter(s => s.id !== servicoId));
  };

  // Atualiza o valor (R$) de um serviço já selecionado
  const atualizarValorServico = (id, valor) => {
    setServicosSelecionados(prev => prev.map(s => s.id === id ? { ...s, valor: parseFloat(valor) || 0 } : s));
  };

  // Calcula total dos serviços selecionados
  const calcularTotal = () => {
    return servicosSelecionados.reduce((acc, s) => acc + s.valor, 0);
  };

  // Solicita confirmação final antes de salvar
  const solicitarConfirmacaoFinal = () => {
    setShowConfirmacaoFinal(true);
  };

  // Confirma manutenção com serviços (executa após confirmação)
  const confirmarManutencaoComServicos = async () => {
    if (!motoManutencao) return;
    
    setShowConfirmacaoFinal(false);
    
    try {
      setSalvandoManutencao(true);
      
      const custoTotal = calcularTotal();

      // Bloqueio: o admin deve ter selecionado ao menos um serviço OU informado um custo total
      if (servicosSelecionados.length === 0 && (!custoTotal || custoTotal <= 0)) {
        setSalvandoManutencao(false);
        alert(
          '⚠️ Antes de confirmar, escolha os serviços realizados ou informe o custo total da manutenção.\n\n' +
          'O sistema NÃO atribui valor padrão automático.'
        );
        return;
      }

      // Chama API para confirmar manutenção COM os serviços
      const response = await apiService.confirmarManutencaoBrasilSat(motoManutencao.id, {
        servicos: servicosSelecionados,
        custo_total: custoTotal
      });
      
      if (response.sucesso) {
        const descontados = response.estoque?.descontados || [];
        const alertasEstoque = response.estoque?.alertas || [];
        alert(
          `✅ Manutenção confirmada com sucesso!\n\n` +
          `KM: ${response.km_manutencao?.toLocaleString() || motoManutencao.km_atual?.toLocaleString()} km\n` +
          `Serviços: ${servicosSelecionados.length > 0 ? servicosSelecionados.map(s => s.nome).join(', ') : 'Manutenção preventiva'}\n` +
          `Custo Total: R$ ${custoTotal.toFixed(2)}\n\n` +
          `Próxima manutenção: ${response.proxima_manutencao_km?.toLocaleString() || 'N/A'} km` +
          (descontados.length > 0 ? `\n\n📦 Estoque abatido:\n${descontados.map(d => `• ${d.item}: -${d.quantidade_usada} (restam ${d.quantidade_restante})`).join('\n')}` : '') +
          (alertasEstoque.length > 0 ? `\n\n⚠️ ${alertasEstoque.map(a => a.mensagem).join('\n⚠️ ')}` : '')
        );
        
        setShowManutencaoModal(false);
        setMotoManutencao(null);
        setServicosSelecionados([]);
        await carregarDados();
      }
    } catch (error) {
      console.error('Erro ao confirmar manutenção:', error);
      const detalhe = error?.response?.data?.detail;
      alert(detalhe ? `⚠️ ${detalhe}` : 'Erro ao confirmar manutenção. Tente novamente.');
    } finally {
      setSalvandoManutencao(false);
    }
  };

  const confirmarManutencao = async (moto) => {
    // Agora abre o modal ao invés de confirm()
    abrirModalManutencao(moto);
  };

  const enviarLembreteManutencao = async (moto) => {
    const confirmacao = confirm(
      `📱 Enviar lembrete de manutenção via WhatsApp?\n\n` +
      `Moto: ${moto.placa}\n` +
      `KM Atual: ${Math.round(moto.km_atual).toLocaleString()} km\n` +
      `Manutenção necessária em: ${moto.proxima_manutencao_km.toLocaleString()} km\n\n` +
      `O cliente receberá uma mensagem automática.`
    );
    
    if (!confirmacao) return;
    
    try {
      const response = await apiService.enviarLembreteManutencao(
        moto.id, 
        moto.proxima_manutencao_km
      );
      
      if (response.sucesso) {
        // Se tiver link do WhatsApp, abrir
        if (response.whatsapp_link) {
          alert(
            `✅ Mensagem preparada!\n\n` +
            `Será aberto o WhatsApp com a mensagem pronta.\n` +
            `Basta clicar em "Enviar" no WhatsApp.`
          );
          window.open(response.whatsapp_link, '_blank');
        } else {
          alert(
            `✅ Lembrete enviado com sucesso!\n\n` +
            `Cliente: ${response.cliente_nome || 'N/A'}\n` +
            `Número: ${response.numero_destino || 'N/A'}`
          );
        }
      } else {
        alert(`❌ Erro: ${response.erro || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao enviar lembrete:', error);
      
      // Mensagens de erro específicas
      if (error.response?.status === 404) {
        alert('⚠️ Não foi possível enviar:\n\nNenhuma locação ativa encontrada para esta moto.');
      } else {
        alert('Erro ao enviar lembrete. Tente novamente.');
      }
    }
  };

  const enviarAvisoBloqueio = async (moto) => {
    const confirmacao = confirm(
      `⚠️ Enviar aviso de bloqueio via WhatsApp?\n\n` +
      `Moto: ${moto.placa}\n` +
      `KM Atual: ${Math.round(moto.km_atual).toLocaleString()} km\n` +
      `Manutenção vencida!\n\n` +
      `O cliente será avisado sobre o bloqueio em 24h.`
    );
    
    if (!confirmacao) return;
    
    try {
      const response = await apiService.enviarAvisoBloqueio(moto.id);
      
      if (response.sucesso) {
        if (response.whatsapp_link) {
          alert(
            `⚠️ Mensagem de bloqueio preparada!\n\n` +
            `Será aberto o WhatsApp com a mensagem de aviso.\n` +
            `Basta clicar em "Enviar" no WhatsApp.`
          );
          window.open(response.whatsapp_link, '_blank');
        } else {
          alert(
            `✅ Aviso enviado com sucesso!\n\n` +
            `Cliente: ${response.cliente_nome || 'N/A'}\n` +
            `Número: ${response.numero_destino || 'N/A'}`
          );
        }
      } else {
        alert(`❌ Erro: ${response.erro || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao enviar aviso:', error);
      
      if (error.response?.status === 404) {
        alert('⚠️ Não foi possível enviar:\n\nNenhuma locação ativa encontrada para esta moto.');
      } else {
        alert('Erro ao enviar aviso. Tente novamente.');
      }
    }
  };

  const formatarDataHora = (isoString) => {
    if (!isoString) return 'Nunca';
    const data = new Date(isoString);
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tempoAtras = (isoString) => {
    if (!isoString) return '';
    try {
      const agora = new Date();
      const data = new Date(isoString);
      
      // Validar se a data é válida
      if (isNaN(data.getTime())) return '';
      
      const diff = Math.floor((agora - data) / 1000);
      
      // Se diff for negativo ou muito grande, retornar vazio
      if (diff < 0 || diff > 31536000) return ''; // Max 1 ano
      
      if (diff < 60) return `${diff}s atrás`;
      if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
      return `${Math.floor(diff / 86400)}d atrás`;
    } catch (error) {
      console.error('Erro ao calcular tempo:', error);
      return '';
    }
  };

  if (loading && !dados) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-300">Carregando dados do GPS...</p>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">❌ {erro}</p>
          <button 
            onClick={carregarDados}
            className="mt-2 text-rose-400 hover:text-red-800 underline"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-2">
              <span className="text-3xl lg:text-4xl">📡</span>
              Brasil Sat GPS - Rastreamento em Tempo Real
            </h1>
            <p className="text-sm lg:text-base text-gray-300 mt-1">Sistema de rastreamento e sincronização automática de quilometragem</p>
            {dados?.ultima_sincronizacao && (
              <p className="text-xs lg:text-sm text-gray-400 mt-1">
                Última sincronização: {formatarDataHora(dados.ultima_sincronizacao)} ({tempoAtras(dados.ultima_sincronizacao)})
              </p>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 items-center">
            <label className="flex items-center gap-2 text-xs lg:text-sm text-gray-300 whitespace-nowrap">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              Auto-atualizar
            </label>
            <button
              onClick={sincronizarAgora}
              disabled={sincronizando || loading}
              className="px-3 lg:px-4 py-2 bg-blue-500 text-white text-xs lg:text-sm rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              {sincronizando ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="hidden lg:inline">Sincronizando...</span>
                  <span className="lg:hidden">...</span>
                </>
              ) : (
                <>
                  <span>🔄</span>
                  <span className="hidden lg:inline">Sincronizar Agora</span>
                  <span className="lg:hidden">Sincronizar</span>
                </>
              )}
            </button>
            <button
              onClick={recalcularManutencoes}
              disabled={recalculando || loading}
              className="px-3 lg:px-4 py-2 bg-orange-500 text-white text-xs lg:text-sm rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              title="Recalcula o km de última manutenção de todas as motos baseado nas manutenções registradas"
            >
              {recalculando ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="hidden lg:inline">Recalculando...</span>
                  <span className="lg:hidden">...</span>
                </>
              ) : (
                <>
                  <span>🔧</span>
                  <span className="hidden lg:inline">Corrigir Manutenções</span>
                  <span className="lg:hidden">Corrigir</span>
                </>
              )}
            </button>
            <button
              onClick={carregarDados}
              disabled={loading}
              className="px-3 lg:px-4 py-2 bg-slate-700/50 text-gray-200 text-xs lg:text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50 whitespace-nowrap"
            >
              🔃 <span className="hidden lg:inline">Atualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-90">Total de Motos</div>
          <div className="text-3xl font-bold mt-1">{dados?.total_motos || 0}</div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-90">Motos Rastreadas</div>
          <div className="text-3xl font-bold mt-1">{dados?.motos_vinculadas || 0}</div>
          <div className="text-xs opacity-75 mt-1">
            {dados?.total_motos > 0 
              ? `${Math.round((dados?.motos_vinculadas / dados?.total_motos) * 100)}% do total`
              : '0%'}
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-90">Dispositivos GPS</div>
          <div className="text-3xl font-bold mt-1">{dados?.total_dispositivos || 0}</div>
          <div className="text-xs opacity-75 mt-1">Disponíveis na Brasil Sat</div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-90">Não Rastreadas</div>
          <div className="text-3xl font-bold mt-1">
            {(dados?.total_motos || 0) - (dados?.motos_vinculadas || 0)}
          </div>
          <div className="text-xs opacity-75 mt-1">Aguardando vinculação</div>
        </div>
      </div>

      {/* Lista de Motos */}
      <div className="bg-slate-800/90 rounded-lg shadow-lg mb-6 overflow-hidden">
        <div className="p-4 border-b border-white/10 bg-slate-800/50">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h2 className="text-base lg:text-lg font-semibold text-white">🏍️ Motos e Rastreamento GPS</h2>
              <p className="text-xs lg:text-sm text-gray-300 mt-1 break-words">Status de rastreamento e informações em tempo real</p>
            </div>
            {/* 🔎 Barra de busca por cliente / placa */}
            <div className="relative w-full lg:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">🔎</span>
              <input
                type="text"
                placeholder="Buscar por placa ou cliente..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-9 pr-9 py-2 bg-slate-900/70 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/40 transition-all"
                data-testid="brasilsat-busca-input"
              />
              {busca && (
                <button
                  onClick={() => setBusca('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 text-sm"
                  data-testid="brasilsat-busca-limpar"
                  title="Limpar busca"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          {busca && (
            <div className="mt-2 text-xs text-gray-400" data-testid="brasilsat-busca-resultado">
              {(() => {
                const q = busca.trim().toLowerCase();
                const n = (dados?.motos || []).filter(m =>
                  (m.placa || '').toLowerCase().includes(q) ||
                  (m.locatario || '').toLowerCase().includes(q)
                ).length;
                return n === 0
                  ? 'Nenhuma moto encontrada.'
                  : `${n} resultado${n > 1 ? 's' : ''} para "${busca}"`;
              })()}
            </div>
          )}
        </div>
        
        {/* Visualização Desktop - Tabela */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Moto</th>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">KM Sistema</th>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">GPS (Tempo Real)</th>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Dispositivo</th>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Status</th>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Manutenção</th>
                <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800/50 divide-y divide-white/10">
              {(() => {
                const q = busca.trim().toLowerCase();
                const motosFiltradas = q
                  ? (dados?.motos || []).filter(m =>
                      (m.placa || '').toLowerCase().includes(q) ||
                      (m.locatario || '').toLowerCase().includes(q)
                    )
                  : (dados?.motos || []);
                return motosFiltradas.map((moto) => (
                <tr key={moto.id} className="hover:bg-slate-800/50">
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-white text-sm flex items-center gap-1.5 flex-wrap">
                      {moto.placa}
                      <CompartilhadaBadge nomes={compartilhamentos.por_moto[moto.id] || compartilhamentos.por_placa[moto.placa]} />
                    </div>
                    <div className="text-xs text-gray-400">{moto.modelo}</div>
                  </td>
                  <td className="px-3 lg:px-6 py-4">
                    {moto.km_atual ? (
                      <div className="text-sm text-white min-w-[100px]">
                        <div className="font-medium">{Math.round(moto.km_atual || 0).toLocaleString()} km</div>
                        {moto.data_atualizacao && <div className="text-xs text-gray-400">{tempoAtras(moto.data_atualizacao)}</div>}
                        <button
                          onClick={() => editarKmManual(moto)}
                          className="text-[10px] text-amber-300 hover:text-amber-200 underline mt-1"
                          data-testid={`btn-editar-km-${moto.placa}`}
                          title="Editar km manualmente (não passa pelo fator do BrasilSat)"
                        >
                          ✏️ Editar km
                        </button>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic whitespace-nowrap">
                        Sem dados
                        <button
                          onClick={() => editarKmManual(moto)}
                          className="block text-[10px] text-amber-300 hover:text-amber-200 underline mt-1"
                          data-testid={`btn-editar-km-${moto.placa}`}
                        >
                          ✏️ Definir km
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 lg:px-6 py-4">
                    {moto.gps_nome ? (
                      <div className="min-w-[180px]">
                        <div className="text-sm font-medium text-white break-words">
                          {moto.gps_nome}
                        </div>
                        {moto.gps_imei && <div className="text-xs text-gray-400 break-all">IMEI: {moto.gps_imei}</div>}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic whitespace-nowrap">Não vinculado</div>
                    )}
                  </td>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    {moto.gps_nome ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 whitespace-nowrap">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1 animate-pulse"></span>
                        Sincronizado
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-gray-300 border border-white/10 whitespace-nowrap">
                        Sem GPS
                      </span>
                    )}
                  </td>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    {moto.km_atual >= moto.proxima_manutencao_km ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-1">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30 flex-1">
                            ⚠️ Manutenção Vencida
                          </span>
                          <button
                            onClick={() => enviarAvisoBloqueio(moto)}
                            className="bg-red-600 text-white p-1.5 rounded hover:bg-red-500 text-xs shadow-md transition-all flex-shrink-0 border border-red-500/50"
                            title="⚠️ Enviar aviso de bloqueio em 24h"
                          >
                            📱
                          </button>
                        </div>
                        <button
                          onClick={() => confirmarManutencao(moto)}
                          className="bg-red-600 text-white px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg hover:bg-red-500 text-xs lg:text-sm font-semibold whitespace-nowrap shadow-md hover:shadow-lg transition-all border border-red-500/50"
                          title="Manutenção vencida! Clique para confirmar que foi realizada"
                          data-testid={`btn-confirmar-manutencao-${moto.placa}`}
                        >
                          ✅ Confirmar Realizada
                        </button>
                        {moto.proxima_manutencao_km_manual && (
                          <div
                            className="text-amber-300 text-xs"
                            data-testid={`badge-ajuste-manual-${moto.placa}`}
                          >
                            🛠️ Ajuste manual: {Number(moto.proxima_manutencao_km_manual).toLocaleString()} km
                          </div>
                        )}
                        <button
                          onClick={() => abrirAjusteManual(moto)}
                          className="text-[11px] text-sky-300 hover:text-sky-200 underline self-start"
                          data-testid={`btn-ajustar-manutencao-${moto.placa}`}
                          title="Definir manualmente em qual KM a próxima manutenção deve ocorrer"
                        >
                          Ajustar manualmente
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400">
                        <div>Próxima: {moto.proxima_manutencao_km?.toLocaleString()} km</div>
                        <div className="text-emerald-400">
                          Faltam: {(moto.proxima_manutencao_km - moto.km_atual)?.toLocaleString()} km
                        </div>
                        {moto.proxima_manutencao_km_manual && (
                          <div
                            className="text-amber-300 mt-1"
                            data-testid={`badge-ajuste-manual-${moto.placa}`}
                          >
                            🛠️ Ajuste manual ativo
                          </div>
                        )}
                        <button
                          onClick={() => confirmarManutencao(moto)}
                          className="mt-2 block bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-500 text-xs font-semibold whitespace-nowrap shadow-md hover:shadow-lg transition-all border border-emerald-500/50"
                          title="Clique para confirmar que a manutenção foi realizada"
                          data-testid={`btn-confirmar-manutencao-${moto.placa}`}
                        >
                          ✅ Confirmar Realizada
                        </button>
                        <button
                          onClick={() => abrirAjusteManual(moto)}
                          className="mt-2 text-[11px] text-sky-300 hover:text-sky-200 underline"
                          data-testid={`btn-ajustar-manutencao-${moto.placa}`}
                          title="Definir manualmente em qual KM a próxima manutenção deve ocorrer"
                        >
                          Ajustar manualmente
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    {moto.gps_imei ? (
                      <div className="flex flex-col lg:flex-row gap-2">
                        <button
                          onClick={() => abrirModalVinculo(moto)}
                          className="text-blue-400 hover:text-blue-800 text-xs lg:text-sm font-medium"
                        >
                          Alterar
                        </button>
                        <button
                          onClick={() => desvincularGps(moto)}
                          className="text-rose-400 hover:text-red-800 text-xs lg:text-sm font-medium"
                        >
                          Desvincular
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => abrirModalVinculo(moto)}
                        className="bg-blue-500 text-white px-2 lg:px-3 py-1 lg:py-1.5 rounded hover:bg-blue-600 text-xs lg:text-sm font-medium whitespace-nowrap"
                      >
                        + Vincular
                      </button>
                    )}
                  </td>
                </tr>
              ));
              })()}
              
              {(!dados?.motos || dados.motos.length === 0) && (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    Nenhuma moto cadastrada no sistema
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Visualização Mobile - Cards */}
        <div className="lg:hidden divide-y divide-white/10">
          {(() => {
            const q = busca.trim().toLowerCase();
            const motosFiltradas = q
              ? (dados?.motos || []).filter(m =>
                  (m.placa || '').toLowerCase().includes(q) ||
                  (m.locatario || '').toLowerCase().includes(q)
                )
              : (dados?.motos || []);
            if (motosFiltradas.length === 0 && q) {
              return (
                <div className="p-8 text-center text-gray-400 text-sm">
                  Nenhuma moto encontrada para &quot;{busca}&quot;.
                </div>
              );
            }
            return motosFiltradas.map((moto) => (
            <div key={moto.id} className="p-4 hover:bg-slate-800/50">
              {/* Header do Card */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-lg text-white flex items-center gap-1.5 flex-wrap">
                    {moto.placa}
                    <CompartilhadaBadge nomes={compartilhamentos.por_moto[moto.id] || compartilhamentos.por_placa[moto.placa]} />
                  </h3>
                  <p className="text-sm text-gray-400">{moto.modelo}</p>
                  {moto.locatario && (
                    <p className="text-sm text-blue-400 font-medium mt-1">
                      👤 Locatário: {moto.locatario}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {moto.gps_nome ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1 animate-pulse"></span>
                      Sincronizado
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-gray-300 border border-white/10">
                      Sem GPS
                    </span>
                  )}
                  {moto.esta_alugada && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      🏍️ Alugada
                    </span>
                  )}
                </div>
              </div>

              {/* Informações em Grid */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* KM Sistema */}
                <div className="bg-slate-800/50 p-3 rounded-lg">
                  <div className="text-xs text-gray-400 mb-1">KM Sistema</div>
                  <div className="font-semibold text-white">{Math.round(moto.km_atual).toLocaleString()} km</div>
                  {moto.data_atualizacao && tempoAtras(moto.data_atualizacao) && (
                    <div className="text-xs text-gray-400 mt-0.5">{tempoAtras(moto.data_atualizacao)}</div>
                  )}
                  <button
                    onClick={() => editarKmManual(moto)}
                    className="text-[10px] text-amber-300 hover:text-amber-200 underline mt-1.5"
                    data-testid={`btn-editar-km-mobile-${moto.placa}`}
                  >
                    ✏️ Editar km
                  </button>
                </div>

                {/* KM Atual */}
                <div className={`p-3 rounded-lg border ${moto.km_atual ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-800/50 border-white/10'}`}>
                  <div className="text-xs text-gray-400 mb-1">KM Atual</div>
                  {moto.km_atual ? (
                    <>
                      <div className="font-semibold text-blue-300">{Math.round(moto.km_atual || 0).toLocaleString()} km</div>
                      {moto.data_atualizacao && (
                        <div className="text-xs text-gray-400 mt-0.5">{tempoAtras(moto.data_atualizacao)}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-gray-400 italic">Sem dados</div>
                  )}
                </div>
              </div>

              {/* Dispositivo */}
              {moto.gps_nome && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-lg mb-3">
                  <div className="text-xs text-emerald-400 mb-1">Dispositivo GPS</div>
                  <div className="font-medium text-sm text-white">{moto.gps_nome}</div>
                  {moto.gps_imei && <div className="text-xs text-gray-300 break-all mt-0.5">IMEI: {moto.gps_imei}</div>}
                </div>
              )}

              {/* Manutenção */}
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg mb-3">
                <div className="text-xs text-amber-400 mb-2 font-semibold">⚠️ Manutenção Preventiva</div>
                {moto.km_atual >= moto.proxima_manutencao_km ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1.5 rounded text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30 flex-1">
                        ⚠️ Manutenção Vencida
                      </span>
                      <button
                        onClick={() => enviarAvisoBloqueio(moto)}
                        className="bg-red-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-red-500 shadow-md border border-red-500/50"
                        title="⚠️ Enviar aviso de bloqueio em 24h"
                      >
                        📱 Aviso
                      </button>
                    </div>
                    <button
                      onClick={() => confirmarManutencao(moto)}
                      className="w-full bg-red-600 text-white px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-red-500 shadow-md border border-red-500/50"
                      data-testid={`btn-confirmar-manutencao-mobile-${moto.placa}`}
                    >
                      ✅ Confirmar Realizada aos {moto.proxima_manutencao_km?.toLocaleString()} km
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm text-gray-200">Próxima: {moto.proxima_manutencao_km?.toLocaleString()} km</div>
                    <div className="text-sm text-emerald-400 font-medium">
                      Faltam: {(moto.proxima_manutencao_km - moto.km_atual)?.toLocaleString()} km
                    </div>
                    {moto.proxima_manutencao_km_manual && (
                      <div
                        className="text-xs text-amber-300 mt-1"
                        data-testid={`badge-ajuste-manual-mobile-${moto.placa}`}
                      >
                        🛠️ Ajuste manual: {Number(moto.proxima_manutencao_km_manual).toLocaleString()} km
                      </div>
                    )}
                    <button
                      onClick={() => confirmarManutencao(moto)}
                      className="w-full mt-2 bg-emerald-600 text-white px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-500 shadow-md border border-emerald-500/50"
                      data-testid={`btn-confirmar-manutencao-mobile-${moto.placa}`}
                    >
                      ✅ Confirmar Realizada
                    </button>
                  </div>
                )}

                {/* Botão Ajustar manualmente o KM da próxima manutenção */}
                <button
                  onClick={() => abrirAjusteManual(moto)}
                  className="w-full mt-2 bg-sky-600/80 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-sky-500 border border-sky-500/50"
                  data-testid={`btn-ajustar-manutencao-mobile-${moto.placa}`}
                  title="Definir manualmente em qual KM a próxima manutenção deve ocorrer"
                >
                  🎯 Ajustar KM da Próxima Manutenção
                </button>

                {/* Botão Manutenção Avulsa - sempre visível */}
                <button
                  onClick={() => abrirManutencaoAvulsa(moto)}
                  className="w-full mt-2 bg-purple-600/80 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-purple-500 border border-purple-500/50"
                  title="Registrar manutenção que NÃO afeta o ciclo de troca de óleo (ex: pneu, retrovisor)"
                >
                  🔧 Manutenção Avulsa (pneu, etc.)
                </button>
              </div>

              {/* Ações */}
              <div className="flex gap-2">
                {moto.gps_imei ? (
                  <>
                    <button
                      onClick={() => abrirModalVinculo(moto)}
                      className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
                    >
                      Alterar GPS
                    </button>
                    <button
                      onClick={() => desvincularGps(moto)}
                      className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
                    >
                      Desvincular
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => abrirModalVinculo(moto)}
                    className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
                  >
                    + Vincular GPS
                  </button>
                )}
              </div>
            </div>
          ));
          })()}
          
          {(!dados?.motos || dados.motos.length === 0) && (
            <div className="p-8 text-center text-gray-400">
              Nenhuma moto cadastrada no sistema
            </div>
          )}
        </div>
      </div>

      {/* Lista de Dispositivos Disponíveis */}
      <div className="bg-slate-800/90 rounded-lg shadow-lg overflow-hidden">
        <div className="p-4 border-b border-white/10 bg-slate-800/50">
          <h2 className="text-base lg:text-lg font-semibold text-white">📱 Dispositivos GPS Disponíveis</h2>
          <p className="text-xs lg:text-sm text-gray-300 mt-1">
            {dados?.total_dispositivos || 0} dispositivos encontrados na Brasil Sat GPS
          </p>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {dados?.dispositivos?.map((disp, idx) => (
              <div 
                key={idx}
                className={`border-2 rounded-lg p-4 transition-all duration-200 ${
                  disp.vinculado 
                    ? 'bg-gradient-to-br from-emerald-600 to-teal-700 border-green-400 shadow-md' 
                    : 'bg-slate-700/50 border-white/10 text-white-white/10 hover:border-blue-400 hover:shadow-lg'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-semibold text-gray-400 uppercase">Dispositivo #{idx + 1}</div>
                  {disp.vinculado && (
                    <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-medium shadow-sm">
                      ✓ Vinculado
                    </span>
                  )}
                </div>
                
                <div className="space-y-2.5">
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Nome</div>
                    <div className="font-semibold text-sm text-white break-words leading-tight">{disp.devicename}</div>
                  </div>
                  
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2">
                    <div className="text-xs text-blue-400 mb-0.5 font-medium">Placa GPS</div>
                    <div className="font-bold text-base text-blue-300">{disp.platenumber}</div>
                  </div>
                  
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">IMEI</div>
                    <div className="font-mono text-xs text-gray-200 break-all bg-slate-800/50 p-1.5 rounded">{disp.imei}</div>
                  </div>
                  
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">SIM Card</div>
                    <div className="text-xs text-gray-200 break-words">{disp.simcard}</div>
                  </div>
                  
                  {disp.tem_tracking && disp.km_atual > 0 && (
                    <div className="pt-2 mt-2 border-t border-white/10">
                      <div className="text-xs text-gray-400 mb-1">Quilometragem Atual</div>
                      <div className="text-xl font-bold text-emerald-400">{disp.km_atual.toLocaleString()} km</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {(!dados?.dispositivos || dados.dispositivos.length === 0) && (
              <div className="col-span-full text-center py-8 text-gray-400">
                Nenhum dispositivo GPS encontrado na Brasil Sat
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Vinculação */}
      {vincularMoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/90 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white rounded-t-xl">
              <h3 className="text-xl font-bold mb-1">
                🔗 Vincular GPS
              </h3>
              <p className="text-blue-100 text-sm">
                Moto: <span className="font-semibold">{vincularMoto.placa}</span> - {vincularMoto.modelo}
              </p>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-300 mb-4 bg-blue-50 p-3 rounded-lg border border-blue-200">
                💡 <strong>Dica:</strong> Procure pela <strong>Placa GPS</strong> que corresponde à placa da sua moto.
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-200 mb-3">
                  Selecione o Dispositivo GPS
                </label>
                <select
                  value={imeiSelecionado}
                  onChange={(e) => setImeiSelecionado(e.target.value)}
                  className="w-full border-2 border-white/20 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">-- Selecione um dispositivo GPS --</option>
                  {dados?.dispositivos?.map((disp, idx) => (
                    <option key={idx} value={disp.imei}>
                      🚗 {disp.platenumber} | {disp.devicename} | IMEI: {disp.imei}
                      {disp.vinculado ? ' ⚠️ [Já vinculado]' : ''}
                      {disp.km_atual > 0 ? ` | ${disp.km_atual} km` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setVincularMoto(null)}
                  className="flex-1 px-4 py-3 border-2 border-white/20 rounded-lg text-gray-200 font-semibold hover:bg-slate-800/50 transition-all"
                  disabled={salvandoVinculo}
                >
                  Cancelar
                </button>
                <button
                  onClick={salvarVinculo}
                  disabled={!imeiSelecionado || salvandoVinculo}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {salvandoVinculo ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Salvando...
                    </>
                  ) : (
                    <>✓ Confirmar Vinculação</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Manutenção com Serviços */}
      {showManutencaoModal && motoManutencao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/90 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-t-2xl">
              <h3 className="text-lg font-bold">🔧 Confirmar Manutenção</h3>
              <p className="text-sm opacity-90 mt-1">
                {motoManutencao.placa} • {Math.round(motoManutencao.km_atual).toLocaleString()} km
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Itens do Estoque */}
              <div>
                <h4 className="font-semibold text-gray-200 mb-1">📦 Itens Utilizados (Estoque)</h4>
                <p className="text-xs text-gray-400 mb-2">Os itens selecionados serão abatidos automaticamente do estoque.</p>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                  {itensEstoque.map(item => {
                    const selecionado = servicosSelecionados.find(s => s.id === item.id);
                    const isServico = item.unidade === 'serviço';
                    const semEstoque = (item.quantidade || 0) <= 0;
                    return (
                      <button
                        key={item.id}
                        onClick={() => toggleServico({ id: item.id, nome: item.nome, valor: item.preco_unitario || 0, item_estoque_id: item.id, quantidade: 1 })}
                        data-testid={`item-estoque-${item.id}`}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selecionado 
                            ? 'border-green-500 bg-green-500/10 text-emerald-300' 
                            : 'border-white/10 text-gray-200 hover:border-green-300 hover:bg-slate-800/50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">{item.nome}</span>
                          {selecionado && <span className="text-green-500">✓</span>}
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-gray-400">
                            {item.preco_unitario > 0 ? `R$ ${Number(item.preco_unitario).toFixed(2)}` : 'R$ —'}
                          </span>
                          {isServico ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-sky-500/20 text-sky-300">
                              serviço
                            </span>
                          ) : (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${semEstoque ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                              {item.quantidade || 0} em estoque
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {itensEstoque.length === 0 && (
                    <p className="text-sm text-gray-500 col-span-2">Carregando itens do estoque...</p>
                  )}
                </div>
              </div>
              
              {/* Serviços Customizados */}
              {servicosCustomizados.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-200 mb-2">⚙️ Meus Serviços</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {servicosCustomizados.map(servico => {
                      const selecionado = servicosSelecionados.find(s => s.id === servico.id);
                      return (
                        <div key={servico.id} className="relative">
                          <button
                            onClick={() => toggleServico(servico)}
                            className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                              selecionado 
                                ? 'border-blue-500 bg-blue-50 text-blue-300' 
                                : 'border-white/10 hover:border-blue-300 hover:bg-slate-800/50'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-sm">{servico.nome}</span>
                              {selecionado && <span className="text-blue-500">✓</span>}
                            </div>
                            <span className="text-sm text-gray-400">
                              R$ {servico.valor.toFixed(2)}
                            </span>
                          </button>
                          <button
                            onClick={() => removerServicoCustom(servico.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Adicionar Novo Serviço */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-200 mb-2">➕ Adicionar Novo Serviço</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Nome do serviço"
                    value={novoServicoNome}
                    onChange={(e) => setNovoServicoNome(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Valor"
                    value={novoServicoValor}
                    onChange={(e) => setNovoServicoValor(e.target.value)}
                    className="w-24 px-3 py-2 border rounded-lg text-sm"
                    step="0.01"
                  />
                  <button
                    onClick={adicionarServicoCustom}
                    disabled={!novoServicoNome.trim() || !novoServicoValor}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    +
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Serviços adicionados ficam salvos para próximas manutenções
                </p>
              </div>
              
              {/* Resumo */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Total dos Serviços:</span>
                  <span className="text-xl font-bold text-emerald-400">
                    R$ {calcularTotal().toFixed(2)}
                  </span>
                </div>
                {servicosSelecionados.length > 0 && (
                  <div className="mt-3 space-y-1.5 border-t border-white/5 pt-2">
                    {servicosSelecionados.map(s => (
                      <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-300 truncate">
                          {s.item_estoque_id ? '📦 ' : ''}{s.nome}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-500">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={s.valor}
                            onChange={(e) => atualizarValorServico(s.id, e.target.value)}
                            className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-right text-white text-xs"
                            data-testid={`valor-servico-${s.id}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowManutencaoModal(false);
                    setMotoManutencao(null);
                    setServicosSelecionados([]);
                  }}
                  className="flex-1 px-4 py-3 border-2 border-white/20 text-gray-300 rounded-lg font-semibold hover:bg-slate-800/50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={solicitarConfirmacaoFinal}
                  disabled={salvandoManutencao}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {salvandoManutencao ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Salvando...
                    </>
                  ) : (
                    <>✓ Confirmar Manutenção</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação Final */}
      {showConfirmacaoFinal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black/70" onClick={() => setShowConfirmacaoFinal(false)}></div>
            <div className="relative bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-white/10">
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center border border-amber-500/30">
                  <span className="text-2xl">⚠️</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-400">Confirmação Necessária</h3>
                  <p className="text-sm text-gray-400">Esta ação não pode ser desfeita</p>
                </div>
              </div>

              <div className="bg-slate-700/50 rounded-xl p-4 mb-4 border border-white/10">
                <p className="text-white font-semibold mb-3">Confirmar manutenção preventiva?</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Moto:</span>
                    <span className="text-white font-medium">{motoManutencao?.placa}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">KM Atual:</span>
                    <span className="text-white font-medium">{Math.round(motoManutencao?.km_atual || 0).toLocaleString()} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Serviços:</span>
                    <span className="text-white font-medium">{servicosSelecionados.length > 0 ? servicosSelecionados.length : 'Preventiva básica'}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-gray-400">Custo Total:</span>
                    <span className="text-emerald-400 font-bold">R$ {calcularTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-300 text-sm font-medium flex items-center gap-2">
                  <span>⚠️</span>
                  Atenção: Esta ação é irreversível!
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmacaoFinal(false)}
                  className="flex-1 px-4 py-3 border border-white/20 text-gray-300 rounded-lg font-semibold hover:bg-slate-700/50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarManutencaoComServicos}
                  disabled={salvandoManutencao}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-500 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {salvandoManutencao ? 'Salvando...' : '✓ Sim, Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Manutenção Avulsa */}
      {showManutencaoAvulsa && motoAvulsa && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl border border-white/10">
            <div className="p-6">
              <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-center gap-2">
                🔧 Manutenção Avulsa
              </h3>
              
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-4">
                <p className="text-purple-300 text-sm">
                  Esta manutenção será registrada no histórico mas <strong>NÃO afetará</strong> o ciclo de troca de óleo.
                </p>
              </div>
              
              <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
                <div className="text-sm text-gray-300">
                  <strong>Moto:</strong> {motoAvulsa.placa} - {motoAvulsa.modelo}
                </div>
                <div className="text-sm text-gray-400">
                  KM Atual: {motoAvulsa.km_atual?.toLocaleString() || 0} km
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Tipo de Manutenção *</label>
                  <select
                    value={tipoManutencaoAvulsa}
                    onChange={handleTipoAvulsa}
                    data-testid="select-tipo-avulsa"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  >
                    <option value="">Selecione...</option>
                    {itensEstoque.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.nome}
                        {item.unidade !== 'serviço' ? ` (${item.quantidade || 0} em estoque)` : ''}
                      </option>
                    ))}
                    <option value="Outro">Outro (especificar)</option>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Itens do estoque selecionados são abatidos automaticamente.
                  </p>
                </div>
                
                {tipoManutencaoAvulsa === 'Outro' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Descrição *</label>
                    <input
                      type="text"
                      value={descricaoAvulsa}
                      onChange={(e) => setDescricaoAvulsa(e.target.value)}
                      placeholder="Descreva a manutenção"
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Valor (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={valorAvulsa}
                    onChange={(e) => setValorAvulsa(e.target.value)}
                    placeholder="0,00"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowManutencaoAvulsa(false);
                    setMotoAvulsa(null);
                  }}
                  className="flex-1 px-4 py-3 border border-white/20 text-gray-300 rounded-lg font-semibold hover:bg-slate-700/50"
                >
                  Cancelar
                </button>
                <button
                  onClick={salvarManutencaoAvulsa}
                  disabled={salvandoAvulsa || !tipoManutencaoAvulsa}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-500 disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {salvandoAvulsa ? 'Salvando...' : '✓ Registrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ajuste Manual da Próxima Manutenção */}
      {showAjusteManual && motoAjuste && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-amber-500/40 rounded-xl max-w-md w-full p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                🛠️ Ajuste Manual de Manutenção
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                Moto <strong>{motoAjuste.placa}</strong> · KM atual:{' '}
                <strong>{(motoAjuste.km_atual || 0).toLocaleString()} km</strong>
              </p>
              <p className="text-xs text-gray-400">
                Última manutenção: {(motoAjuste.km_ultima_manutencao || 0).toLocaleString()} km
              </p>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-xs text-amber-200">
              Defina <strong>em qual KM</strong> a próxima manutenção deve ocorrer.
              Após a manutenção ser confirmada, o sistema volta automaticamente
              ao padrão de <strong>2.000 km</strong>.
            </div>

            <div>
              <label className="text-sm text-gray-300">Próxima manutenção em (km)</label>
              <input
                type="number"
                value={kmAjuste}
                onChange={(e) => setKmAjuste(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                data-testid="input-km-ajuste-manutencao"
                placeholder="ex.: 9200"
              />
              {kmAjuste && Number(kmAjuste) > (motoAjuste.km_atual || 0) && (
                <p className="text-xs text-emerald-400 mt-1">
                  Vai notificar em {(Number(kmAjuste) - (motoAjuste.km_atual || 0)).toLocaleString()} km
                  a partir do atual.
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { setShowAjusteManual(false); setMotoAjuste(null); }}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Cancelar
              </button>
              {motoAjuste.proxima_manutencao_km_manual && (
                <button
                  onClick={removerAjusteManual}
                  disabled={salvandoAjuste}
                  className="flex-1 px-4 py-2 bg-rose-700 text-white rounded-lg hover:bg-rose-600 disabled:opacity-50"
                  data-testid="btn-remover-ajuste-manutencao"
                >
                  Remover ajuste
                </button>
              )}
              <button
                onClick={salvarAjusteManual}
                disabled={salvandoAjuste || !kmAjuste}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-500 disabled:opacity-50"
                data-testid="btn-salvar-ajuste-manutencao"
              >
                {salvandoAjuste ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrasilSatPage;
